import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, TrendingUp, Download, Calendar, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import PageHeader from '../../components/layout/PageHeader';
import { AZURE } from '../../services/api';
import { useActiveContext } from '../../contexts/ActiveContextContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const FINANCIAL_LABELS = {
  pt: {
    noClub: 'Nenhum clube vinculado. Complete o onboarding primeiro.',
    title: 'Financeiro',
    subtitle: 'Assinatura, faturas e consumo',
    plan: 'Plano',
    active: 'Ativo',
    inactive: 'Inativo',
    nextInvoice: 'Próxima Fatura',
    monthUsage: 'Consumo Mês',
    of: 'de',
    activeModules: 'Módulos Ativos',
    available: 'disponíveis',
    subDetails: 'Detalhes da Assinatura',
    colPlan: 'Plano',
    colStart: 'Início',
    colRenewal: 'Renovação',
    colMonthly: 'Valor Mensal',
    contractedModules: 'Módulos Contratados',
    activeSince: (d) => `Ativo desde ${d}`,
    invoices: 'Faturas',
    noInvoices: 'Nenhuma fatura encontrada.',
    invoiceNum: (n) => `Fatura #${n}`,
    due: 'Vencimento:',
    loadError: 'Não foi possível carregar os dados financeiros.',
    retry: 'Tentar novamente',
    statusMap: {
      paid: { bg: '#d1fae5', text: '#065f46', label: 'Pago' },
      pending: { bg: '#fef3c7', text: '#92400e', label: 'Pendente' },
      overdue: { bg: '#fee2e2', text: '#991b1b', label: 'Vencida' },
      cancelled: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelada' },
    },
  },
  en: {
    noClub: 'No club linked. Complete onboarding first.',
    title: 'Financial',
    subtitle: 'Subscription, invoices & usage',
    plan: 'Plan',
    active: 'Active',
    inactive: 'Inactive',
    nextInvoice: 'Next Invoice',
    monthUsage: 'Month Usage',
    of: 'of',
    activeModules: 'Active Modules',
    available: 'available',
    subDetails: 'Subscription Details',
    colPlan: 'Plan',
    colStart: 'Start',
    colRenewal: 'Renewal',
    colMonthly: 'Monthly Amount',
    contractedModules: 'Contracted Modules',
    activeSince: (d) => `Active since ${d}`,
    invoices: 'Invoices',
    noInvoices: 'No invoices found.',
    invoiceNum: (n) => `Invoice #${n}`,
    due: 'Due:',
    loadError: 'Unable to load financial data.',
    retry: 'Try again',
    statusMap: {
      paid: { bg: '#d1fae5', text: '#065f46', label: 'Paid' },
      pending: { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
      overdue: { bg: '#fee2e2', text: '#991b1b', label: 'Overdue' },
      cancelled: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelled' },
    },
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="h-28 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
);

const StatusBadge = ({ status, t }) => {
  const s = (t?.statusMap || FINANCIAL_LABELS.pt.statusMap)[status] || (t?.statusMap || FINANCIAL_LABELS.pt.statusMap).pending;
  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border p-4" style={{ borderColor: VUKZ.border }}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15', color }}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold" style={{ color: VUKZ.text }}>{value}</p>
        {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Financial Page — Assinatura & Faturas
// ═══════════════════════════════════════════════════════════════════════════════

export default function FinancialPage() {
  const { clubId: ctxClubId } = useActiveContext();
  const { clubId: rbacClubId } = useRBAC();
  const clubId = ctxClubId || rbacClubId;
  const { lang } = useGovernanceLang();
  const t = FINANCIAL_LABELS[lang] || FINANCIAL_LABELS.pt;
  const formatDate = (iso) => { if (!iso) return '—'; return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'pt-BR'); };
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [modules, setModules] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!clubId) return;
    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [subRes, invRes, modRes, usageRes] = await Promise.allSettled([
          AZURE.get(`/billing/subscription?institution_id=${clubId}`),
          AZURE.get(`/billing/invoices?institution_id=${clubId}`),
          AZURE.get(`/billing/modules?institution_id=${clubId}`),
          AZURE.get(`/billing/usage?institution_id=${clubId}`),
        ]);
        // Billing Smoke (jul/2026): falha de endpoint NÃO pode virar sucesso
        // com zeros falsos — "sem dados" (200 com data vazia) ≠ "erro de
        // integração" (request rejeitada). Qualquer rejeição -> estado de erro
        // com retry, em vez de renderizar Free/R$0 silenciosamente.
        const anyFailed = [subRes, invRes, modRes, usageRes].some(r => r.status === 'rejected');
        if (anyFailed) {
          console.error('Financial load error:', [subRes, invRes, modRes, usageRes]
            .filter(r => r.status === 'rejected').map(r => r.reason?.message));
          setLoadError(true);
          return;
        }
        setSubscription(subRes.value.data?.data || subRes.value.data);
        setInvoices(invRes.value.data?.data || invRes.value.data || []);
        setModules(modRes.value.data?.data || modRes.value.data || []);
        setUsage(usageRes.value.data?.data || usageRes.value.data);
      } catch (e) {
        console.error('Financial load error:', e);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clubId, reloadKey]);

  if (!clubId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: VUKZ.bg }}>
        <p className="text-gray-500">{t.noClub}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
        icon={DollarSign}
      />

      <div className="max-w-5xl mx-auto px-4 pb-8 space-y-6">        {/* ─── Error State (erro de integração ≠ sem dados) ─────── */}
        {!loading && loadError ? (
          <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: VUKZ.border }} data-testid="financial-error">
            <AlertTriangle size={28} className="mx-auto mb-3 text-amber-500" />
            <p className="text-sm text-gray-600 mb-4">{t.loadError}</p>
            <button
              onClick={() => setReloadKey(k => k + 1)}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: VUKZ.primary || '#064e3b' }}
            >
              {t.retry}
            </button>
          </div>
        ) : (
        <>        {/* ─── Summary Cards ─────────────────────────────────────── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={CreditCard}
              label={t.plan}
              value={subscription?.plan_name || 'Free'}
              sub={subscription?.status === 'active' ? t.active : t.inactive}
              color="#6366f1"
            />
            <StatCard
              icon={Calendar}
              label={t.nextInvoice}
              value={formatDate(subscription?.next_billing_date)}
              sub={formatCurrency(subscription?.monthly_price)}
              color="#f59e0b"
            />
            <StatCard
              icon={TrendingUp}
              label={t.monthUsage}
              value={usage?.current_month_total ? formatCurrency(usage.current_month_total) : 'R$ 0'}
              sub={`${t.of} ${formatCurrency(subscription?.monthly_price)}`}
              color="#16a34a"
            />
            <StatCard
              icon={CheckCircle}
              label={t.activeModules}
              value={modules.filter(m => m.is_active).length || 0}
              sub={`${t.of} ${modules.length || 0} ${t.available}`}
              color="#8b5cf6"
            />
          </div>
        )}

        {/* ─── Subscription Details ──────────────────────────────── */}
        {subscription && (
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: VUKZ.border }}>
            <h3 className="font-bold text-sm mb-3" style={{ color: VUKZ.text }}>{t.subDetails}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-400 text-xs">{t.colPlan}</p>
                <p className="font-semibold">{subscription.plan_name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">{t.colStart}</p>
                <p className="font-semibold">{formatDate(subscription.start_date)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">{t.colRenewal}</p>
                <p className="font-semibold">{formatDate(subscription.next_billing_date)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">{t.colMonthly}</p>
                <p className="font-semibold">{formatCurrency(subscription.monthly_price)}</p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Active Modules ────────────────────────────────────── */}
        {modules.length > 0 && (
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: VUKZ.border }}>
            <h3 className="font-bold text-sm mb-3" style={{ color: VUKZ.text }}>{t.contractedModules}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {modules.map((mod, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-lg border"
                  style={{ borderColor: mod.is_active ? '#d1fae5' : VUKZ.border, backgroundColor: mod.is_active ? '#f0fdf4' : '#fafafa' }}
                >
                  <div className={`w-2 h-2 rounded-full ${mod.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{mod.module_name}</p>
                    <p className="text-[10px] text-gray-400">
                      {mod.is_active ? t.activeSince(formatDate(mod.activated_at)) : t.inactive}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Invoices Table ────────────────────────────────────── */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: VUKZ.border }}>
            <h3 className="font-bold text-sm" style={{ color: VUKZ.text }}>{t.invoices}</h3>
          </div>

          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 rounded-lg animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {t.noInvoices}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: VUKZ.border }}>
              {invoices.map((inv, i) => (
                <div key={i} className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {t.invoiceNum(inv.invoice_id || i + 1)}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {t.due} {formatDate(inv.due_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={inv.status} t={t} />
                    <p className="text-sm font-bold" style={{ color: VUKZ.text }}>
                      {formatCurrency(inv.amount)}
                    </p>
                    {inv.pdf_url && (
                      <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600">
                        <Download size={14} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
